import React from 'react';
import { useFormContext } from '../../components/FormContext';

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

// Friendly labels for error messages, since field keys like
// "streetAddress" don't read well when capitalised automatically
const FIELD_LABELS = {
  streetAddress: 'Street address',
  suburb: 'Suburb',
  state: 'State',
  postcode: 'Postcode',
  email: 'Email',
  mobile: 'Mobile',
};

const ContactInfoSection = () => {
  const {
    formData,
    formErrors,
    modifiedFields,
    setFieldValue,
    setFormErrors,
    setModifiedFields,
  } = useFormContext();

  const label = (field) => FIELD_LABELS[field] || field;

  const setError = (field, error) => {
    if (error) {
      setFormErrors((prev) => ({ ...prev, [field]: error }));
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const markModified = (field) => {
    setModifiedFields((prev) => new Set([...prev, field]));
  };

  const showError = (field) =>
    formErrors[field] &&
    modifiedFields &&
    typeof modifiedFields.has === 'function' &&
    modifiedFields.has(field);

  // --- Generic required text field (street address, suburb) ---
  const handleTextChange = (field, e) => {
    const value = e.target.value;
    setFieldValue(field, value);
    markModified(field);

    const error = value.trim() === '' ? `${label(field)} is required` : null;
    setError(field, error);
  };

  // --- State select ---
  const handleStateChange = (e) => {
    const value = e.target.value;
    setFieldValue('state', value);
    markModified('state');
    setError('state', value ? null : 'State is required');
  };

  // --- Postcode: exactly 4 digits ---
  const handlePostcodeChange = (e) => {
    // Strip anything that isn't a digit, cap at 4
    const value = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
    setFieldValue('postcode', value);
    markModified('postcode');

    let error = null;
    if (!value) {
      error = 'Postcode is required';
    } else if (value.length !== 4) {
      error = 'Postcode must be 4 digits';
    }
    setError('postcode', error);
  };

  // --- Email ---
  const validateEmail = (field, e) => {
    const value = e.target.value;
    setFieldValue('email', value);
    markModified(field);

    let error = null;
    if (!value) {
      error = `${label(field)} is required`;
    } else {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(String(value).toLowerCase())) {
        error = `Please enter a valid ${label(field).toLowerCase()} address`;
      }
    }
    setError(field, error);
  };

  // --- Mobile: Australian format 0400 000 000 ---
  const processPhoneNumber = (field, e) => {
    const value = e.target.value;
    const digits = value.replace(/[^\d]/g, '');

    let error = null;
    if (!value) {
      error = `${label(field)} is required`;
    } else if (digits.length < 10) {
      error = 'Mobile number must have at least 10 digits';
    }

    if (!value || digits.length < 10) {
      setFieldValue('mobile', value);
    } else {
      // 0400 000 000
      const formattedValue = `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
      setFieldValue('mobile', formattedValue);
    }
    markModified(field);
    setError(field, error);
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

  const ErrorMessage = ({ field }) =>
    showError(field) ? (
      <div className="text-sm text-red-600 mt-1">{formErrors[field]}</div>
    ) : null;

  return (
    <div className="form-section">
      <h4 className="text-lg font-medium text-gray-700 mb-3">Contact Information</h4>

      <div className="space-y-4">
        <div>
          <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1">
            Street address
          </label>
          <input
            type="text"
            id="streetAddress"
            name="streetAddress"
            value={formData.streetAddress}
            onChange={(e) => handleTextChange('streetAddress', e)}
            placeholder="e.g. 12 Smith Street"
            autoComplete="address-line1"
            className={inputClass}
          />
          <ErrorMessage field="streetAddress" />
        </div>

        {/* Suburb / State / Postcode on one row on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="suburb" className="block text-sm font-medium text-gray-700 mb-1">
              Suburb
            </label>
            <input
              type="text"
              id="suburb"
              name="suburb"
              value={formData.suburb}
              onChange={(e) => handleTextChange('suburb', e)}
              autoComplete="address-level2"
              className={inputClass}
            />
            <ErrorMessage field="suburb" />
          </div>

          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <select
              id="state"
              name="state"
              value={formData.state}
              onChange={handleStateChange}
              autoComplete="address-level1"
              className={inputClass}
            >
              <option value="">Select</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <ErrorMessage field="state" />
          </div>

          <div>
            <label htmlFor="postcode" className="block text-sm font-medium text-gray-700 mb-1">
              Postcode
            </label>
            <input
              type="text"
              id="postcode"
              name="postcode"
              inputMode="numeric"
              value={formData.postcode}
              onChange={handlePostcodeChange}
              placeholder="3000"
              autoComplete="postal-code"
              className={inputClass}
            />
            <ErrorMessage field="postcode" />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={(e) => validateEmail('email', e)}
            autoComplete="email"
            className={inputClass}
          />
          <ErrorMessage field="email" />
        </div>

        <div>
          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
            Mobile
          </label>
          <input
            type="text"
            id="mobile"
            name="mobile"
            value={formData.mobile}
            onChange={(e) => processPhoneNumber('mobile', e)}
            placeholder="0400 000 000"
            inputMode="numeric"
            autoComplete="tel"
            className={inputClass}
          />
          <ErrorMessage field="mobile" />
        </div>
      </div>
    </div>
  );
};

export default ContactInfoSection;